import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useLazyGetSwapQuoteQuery } from '../../redux/api/authorized/swap';
import { useSelector } from 'react-redux';
import { selectPrivyToken } from '../../redux/slices/auth';
import { useNavigate } from 'react-router-dom';
import BetsButton from '../Vets/Button';
import {
  selectSolanaUSDCBalance,
  selectTokenBalance,
} from '../../redux/slices/walletSlice';
import { selectTokenBySymbol } from '../../redux/slices/tokenSlice';
import { Container, ModalContainer } from '../BaseModal';
import { formatUSD, hasBalance } from '../../common/currency';
import { UNLEASH_ENABLED_BUY_VETS } from '../../constants';
import { BodySmall } from '../Texts';
import { toTokenAmount } from '../../hooks/useSolana';
import { useFlag } from '@unleash/proxy-client-react';
import { LinkText } from '../BackButton';

const StyledInput = styled.div`
  height: 70px;
  width: 100%;
  padding: 10px 40px;
  background-color: ${({ theme }) => theme.background.primary};
  border: 1px solid ${({ theme }) => theme.text.secondary};
  color: ${({ theme }) => theme.text.secondary};
  font-size: 18px;
  font-family: Manrope;
  font-weight: 400;
  text-align: right;
  display: flex;
  align-items: center;
  flex-direction: row-reverse;
`;

const InputContainer = styled.div`
  display: flex;
  width: fit-content;
  max-width: 719px;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  position: relative;
  cursor: pointer;
  transition: transform 0.3s ease-in-out;
  @media (hover: hover) {
    &:hover {
      transform: scale(1.03);
    }
  }
`;

const Label = styled.p`
  font-family: Manrope;
  color: ${({ theme }) => theme.text.primary};
  font-size: 12px;
  font-weight: 400;
`;

export const ErrorLabel = styled.p`
  margin-top: 5px;
  font-family: Manrope;
  color: ${({ theme }) => theme.status.error.primary};
  font-size: 12px;
  font-weight: 400;
  &::first-letter {
    text-transform: uppercase;
  }
`;

const ImageContainer = styled.div`
  position: absolute;
  top: 0px;
  bottom: 0px;
  left: 0px;
  padding: 0px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const Image = styled.img`
  width: 36px;
  height: 36px;
`;

const NameContainer = styled.div``;

const Name = styled.p`
  color: ${({ theme }) => theme.text.regular};
  font-size: 20px;
  font-weight: 500;
  font-family: Manrope;
`;

const ValueContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: -5px;
`;

const SubValue = styled.p`
  color: ${({ theme }) => theme.text.secondary};
  font-size: 10px;
  font-family: Manrope;
  font-weight: 400;
`;

const Chevron = styled.img`
  position: absolute;
  transform: rotate(-90deg);
  width: 15px;
  top: calc(50% - 3px);
  right: 15px;
  opacity: 0.6;
`;

const TokenInput = ({ tokenType = 'usd', label, value, subvalue, onClick }) => {
  return (
    <>
      <InputContainer onClick={onClick}>
        {label && <Label className='label'>{label}&nbsp;</Label>}
        <StyledInput>
          <ValueContainer>
            <p>{value}</p>
            {subvalue && <SubValue>{subvalue}</SubValue>}
            <Chevron src='/chevron-down.svg' />
          </ValueContainer>
        </StyledInput>
        <ImageContainer>
          {tokenType === 'usd' && (
            <>
              <Image src='/usd.webp' />
              <NameContainer>
                <Name>USD</Name>
              </NameContainer>
            </>
          )}
          {tokenType === 'vets' && (
            <>
              <Image src='/vets.webp' />
              <NameContainer>
                <Name>VETS</Name>
              </NameContainer>
            </>
          )}
          {tokenType === 'usdc' && (
            <>
              <Image src='/usdc.webp' />
              <NameContainer>
                <Name>USDC</Name>
              </NameContainer>
            </>
          )}
        </ImageContainer>
      </InputContainer>
    </>
  );
};

const InnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
`;

const ButtonsContainer = styled.div`
  margin-top: 20px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
`;

const TokensContainer = styled.div`
  display: flex;
  gap: 5px;
  flex-direction: column;
  position: relative;
  margin-top: 30px;
`;

const Title = styled.h1`
  color: ${({ theme }) => theme.text.title};
  font-size: 40px;
  font-weight: 600;
  font-family: Manrope;
`;

const BalanceLabel = styled.p`
  color: ${({ theme }) => theme.text.regular};
  font-size: 16px;
  font-weight: 400;
  font-family: Manrope;
  margin-top: 10px;
  margin-bottom: -5px;
`;

const TotalBalance = styled.p`
  color: ${({ theme }) => theme.text.regular};
  font-size: 36px;
  font-weight: 600;
  font-family: Manrope;
`;

const Wallet = () => {
  const usdcBalance = useSelector(selectSolanaUSDCBalance);
  const vetsToken = useSelector((state) =>
    selectTokenBySymbol(state, 'solana', 'VETS'),
  );
  const vetsBalance = useSelector((state) =>
    selectTokenBalance('solana', vetsToken?.address || '')(state),
  );

  const enableBuyVets = useFlag(UNLEASH_ENABLED_BUY_VETS);

  const [vetsUsdBalance, setVetsUsdBalance] = useState(0);
  const privyToken = useSelector(selectPrivyToken);

  const navigate = useNavigate();
  const [triggerGetSwapQuote] = useLazyGetSwapQuoteQuery();

  const vetsTokenAddress = vetsToken?.address;

  useEffect(() => {
    const fetchQuote = async () => {
      const resp = await triggerGetSwapQuote({
        inputMint: vetsTokenAddress,
        outputMint: import.meta.env.VITE_SOLANA_USDC_MINT,
        amount: toTokenAmount(vetsBalance, 9),
      }).unwrap();
      const price = parseInt(resp.quote.outAmount) / 1000000;
      setVetsUsdBalance(price);
    };

    if (privyToken && vetsBalance && vetsTokenAddress) {
      fetchQuote();
    }
  }, [triggerGetSwapQuote, privyToken, vetsBalance, vetsTokenAddress]);

  return (
    <Container>
      <ModalContainer>
        <InnerContainer>
          <Title>My Wallet</Title>
          <BalanceLabel>Total balance</BalanceLabel>
          <TotalBalance>
            {formatUSD(Number(usdcBalance) + Number(vetsUsdBalance))}
          </TotalBalance>
          <TokensContainer>
            <TokenInput
              onClick={() => {
                navigate('/app/usd');
              }}
              value={`${formatUSD(usdcBalance)}`}
            />
            <TokenInput
              onClick={() => {
                if (enableBuyVets) {
                  navigate('/app/vets');
                }
              }}
              value={`${formatUSD(vetsUsdBalance)}`}
              tokenType='vets'
              subvalue={!enableBuyVets ? undefined : `${vetsBalance} VETS`}
            />
          </TokensContainer>
          <ButtonsContainer>
            <BetsButton
              disabled={!enableBuyVets}
              fullWidth
              onClick={() => {
                if (enableBuyVets) {
                  if (hasBalance(usdcBalance)) {
                    navigate('/app/vets/buy');
                  } else {
                    navigate('/app/deposit-selection');
                  }
                }
              }}
            >
              Buy VETS
            </BetsButton>
            {enableBuyVets && (
              <LinkText
                style={{ textAlign: 'right', width: '100%' }}
                onClick={async () => {
                  navigate('/app/gifts/new');
                }}
              >
                Or Gift VETS
              </LinkText>
            )}
            {!enableBuyVets && (
              <BodySmall>VETS is not available for purchase yet.</BodySmall>
            )}
          </ButtonsContainer>
        </InnerContainer>
      </ModalContainer>
    </Container>
  );
};

export default Wallet;
